import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLang } from '../../context/LangContext';
import logo from '../../assets/logos/cetificated.png';
import './Wizard.css';
import enTexts from '../../translations/en.json';
import esTexts from '../../translations/es.json';
import { formatDestinationName as formatDest, sortDestinations } from '../../utils/destination';

const Wizard = () => {
  const { type } = useParams(); // 'incoming' or 'outgoing'
  const navigate = useNavigate();
  const { lang } = useLang();
  const texts = lang === 'en' ? enTexts : esTexts;

  const [step, setStep] = useState(1);
  const [destinations, setDestinations] = useState([]);

  // Step 1: User Data
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');

  // Step 2: Destination Selection
  const [selectedDestination, setSelectedDestination] = useState(null);

  // Step 3: OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const otpInputs = useRef([]);

  const destType = type === 'incoming' ? 'city' : 'country';

  const formatDestinationName = dest => formatDest(dest, texts);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/destinations?type=${destType}`
        );
        setDestinations(sortDestinations(response.data));
      } catch (error) {
        console.error('Error fetching destinations:', error);
      }
    };
    fetchDestinations();
  }, [type, lang, destType]);

  const handleNextStep1 = () => {
    if (userName.trim() !== '' && userEmail.trim().includes('@')) {
      setStep(2);
    }
  };

  const sendOtp = async destinationId => {
    setIsSendingOtp(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/leads/send-otp`, {
        name: userName,
        email: userEmail,
        destination_id: destinationId,
      });
      setStep(3); // Go to OTP step
    } catch (error) {
      console.error('Error sending OTP:', error);
      alert(texts.wizard.error_email);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleSelectDest = dest => {
    setSelectedDestination(dest);
  };

  const handleOtpChange = (index, value) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto focus next input
    if (value !== '' && index < 5) {
      setTimeout(() => {
        otpInputs.current[index + 1]?.focus();
      }, 10);
    }

    setOtpError('');
  };

  const handleOtpPaste = e => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    // Check if it contains exactly 6 digits
    const digits = pastedData.replace(/\D/g, '');
    if (digits.length === 6) {
      setOtp(digits.split(''));
      setTimeout(() => {
        otpInputs.current[5]?.focus();
      }, 10);
      setOtpError('');
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      setTimeout(() => {
        otpInputs.current[index - 1]?.focus();
      }, 10);
    }
  };

  const verifyOtp = async code => {
    setIsVerifying(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/leads/verify-otp`, {
        email: userEmail,
        otp: code,
      });
      setStep(4); // Go to finish
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setOtpError(texts.wizard.error_code);
      setOtp(['', '', '', '', '', '']);
      otpInputs.current[0].focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFinish = () => {
    let text = texts.wizard.whatsapp_msg;
    text = text
      .replace('{destination}', formatDestinationName(selectedDestination))
      .replace('{name}', userName);

    const encodedText = encodeURIComponent(text);
    const waUrl = selectedDestination.whatsapp_url;

    let finalUrl = waUrl;
    if (waUrl.includes('api.whatsapp.com') || waUrl.includes('wa.me')) {
      const separator = waUrl.includes('?') ? '&' : '?';
      finalUrl = `${waUrl}${separator}text=${encodedText}`;
    }

    window.open(finalUrl, '_blank');
  };

  const handleReturnHome = () => {
    navigate('/');
  };

  return (
    <div className="wizard-container">
      <div className="wizard-content">
        {/* Header / Logo */}
        <div className="wizard-header text-center">
          <img src={logo} alt="Logo" className="rounded-circle wizard-logo mb-4" />

          {/* Progress Bar */}
          <div className="wizard-progress">
            <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1</div>
            <div className={`progress-arrow ${step >= 2 ? 'active' : ''}`}>→</div>
            <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2</div>
            <div className={`progress-arrow ${step >= 3 ? 'active' : ''}`}>→</div>
            <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>3</div>
            <div className={`progress-arrow ${step >= 4 ? 'active' : ''}`}>→</div>
            <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>4</div>
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: step === 1 ? '8%' : `${((step - 1) / 3) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* STEP 1: User Data */}
        {step === 1 && (
          <div className="wizard-step text-center">
            <h2 className="step-title">{texts.wizard.title_data}</h2>
            <p className="step-subtitle">{texts.wizard.subtitle_data}</p>

            <div className="input-container mt-4">
              <input
                type="text"
                className="text-input mb-3"
                placeholder={texts.wizard.placeholder_name}
                value={userName}
                onChange={e => setUserName(e.target.value)}
              />
              <input
                type="email"
                className="text-input"
                placeholder={texts.wizard.placeholder_email}
                value={userEmail}
                onChange={e => setUserEmail(e.target.value)}
              />
            </div>

            <div className="wizard-footer">
              <button
                className="btn-wizard btn-orange"
                onClick={handleNextStep1}
                disabled={!userName.trim() || !userEmail.trim().includes('@')}
              >
                {texts.wizard.btn_next}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Destinations (List) */}
        {step === 2 && (
          <div className="wizard-step text-center">
            <h2 className="step-title">
              {type === 'incoming' ? texts.wizard.title_incoming : texts.wizard.title_outgoing}
            </h2>
            <p className="step-subtitle">{texts.wizard.subtitle_search}</p>

            <div className="semester-list" style={{ maxHeight: '400px' }}>
              {destinations.map(dest => (
                <button
                  key={dest.id}
                  className={`btn-semester ${selectedDestination?.id === dest.id ? 'selected' : ''}`}
                  onClick={() => handleSelectDest(dest)}
                  disabled={isSendingOtp}
                >
                  {formatDestinationName(dest)}
                  <i
                    className={`fa ${selectedDestination?.id === dest.id ? 'fa-check-circle' : 'fa-chevron-right'}`}
                    style={{ opacity: selectedDestination?.id === dest.id ? 1 : 0.5 }}
                  ></i>
                </button>
              ))}
              {destinations.length === 0 && (
                <p className="text-white mt-3">{texts.wizard.no_destinations}</p>
              )}
            </div>

            <div className="wizard-footer d-flex justify-content-between w-100">
              <button
                className="btn-wizard btn-grey"
                onClick={() => setStep(1)}
                disabled={isSendingOtp}
              >
                {texts.wizard.btn_back}
              </button>
              <button
                className="btn-wizard btn-orange"
                onClick={() => sendOtp(selectedDestination.id)}
                disabled={!selectedDestination || isSendingOtp}
              >
                {isSendingOtp ? texts.wizard.sending : texts.wizard.btn_next}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: OTP Verification */}
        {step === 3 && (
          <div className="wizard-step text-center">
            <h2 className="step-title">{texts.wizard.title_code}</h2>
            <p className="step-subtitle">{texts.wizard.subtitle_code}</p>

            <div className="otp-container mt-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="1"
                  className={`otp-input ${otpError ? 'error' : ''}`}
                  value={digit}
                  onChange={e => handleOtpChange(index, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(index, e)}
                  onPaste={index === 0 ? handleOtpPaste : undefined}
                  ref={el => (otpInputs.current[index] = el)}
                  disabled={isVerifying}
                />
              ))}
            </div>

            {otpError && (
              <p className="text-danger mb-2" style={{ color: '#ef4444', fontWeight: 600 }}>
                {otpError}
              </p>
            )}

            <button
              className="btn btn-link text-white text-decoration-none mt-2"
              onClick={() => sendOtp(selectedDestination.id)}
              disabled={isSendingOtp}
            >
              {texts.wizard.btn_resend}
            </button>

            <div className="wizard-footer d-flex justify-content-between w-100">
              <button
                className="btn-wizard btn-grey"
                onClick={() => setStep(2)}
                disabled={isVerifying}
              >
                {texts.wizard.btn_back}
              </button>
              <button
                className="btn-wizard btn-orange"
                onClick={() => verifyOtp(otp.join(''))}
                disabled={otp.some(v => v === '') || isVerifying}
              >
                {isVerifying ? texts.wizard.verifying : texts.wizard.btn_next}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Finish */}
        {step === 4 && (
          <div className="wizard-step text-center">
            <h2 className="step-title">{texts.wizard.title_finish}</h2>
            <p className="step-subtitle">{texts.wizard.subtitle_finish}</p>

            <div className="summary-card mt-4 text-start">
              <div className="d-flex justify-content-between mb-3">
                <span className="summary-label">{texts.wizard.label_destination}</span>
                <span className="summary-value fw-bold text-end ms-3">
                  {formatDestinationName(selectedDestination)}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span className="summary-label">{texts.wizard.label_name}</span>
                <span className="summary-value fw-bold">{userName}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="summary-label">{texts.wizard.label_email}</span>
                <span className="summary-value fw-bold">{userEmail}</span>
              </div>
            </div>

            <div className="wizard-footer-full d-flex flex-column gap-3 mt-4">
              <button
                className="btn-wizard btn-orange w-100"
                onClick={() => {
                  const slug =
                    selectedDestination?.slug ||
                    selectedDestination?.name
                      ?.toLowerCase()
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .replace(/\s+/g, '-');
                  navigate(`/page/${slug}`);
                }}
              >
                <i className="fa fa-search me-2"></i>
                {lang === 'en' ? 'View accommodation options' : 'Ver opciones de alojamiento'}
              </button>
              <button className="btn-wizard btn-whatsapp w-100" onClick={handleFinish}>
                <i className="fa fa-whatsapp me-2"></i>
                {texts.wizard.btn_whatsapp.replace(
                  '{destination}',
                  formatDestinationName(selectedDestination)
                )}
              </button>
              <button className="btn-wizard btn-grey w-100" onClick={handleReturnHome}>
                {texts.wizard.btn_home}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wizard;
